import React, { useCallback, useEffect, useState } from 'react';
import { Banner, Button, Card, EmptyState, Input, Select, Spinner, Switch, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useProperty } from '../../app/PropertyContext';
import { settingsApi } from './settingsApi';

const DEFAULT_MAX_MB = 25;
const ABSOLUTE_MAX_MB = 200;

/**
 * Requisition rules (organization default plus optional per-property override) and the
 * organization-wide upload size ceiling, which applies to every file in the system.
 */
export default function RequisitionSettingsTab() {
  const toast = useToast();
  const t = useT();
  const { properties, loaded: propsLoaded } = useProperty();

  const [orgRequire, setOrgRequire] = useState(false);
  const [maxMb, setMaxMb] = useState(String(DEFAULT_MAX_MB));
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [propOverride, setPropOverride] = useState(false);
  const [propRequire, setPropRequire] = useState(false);
  const [propLoading, setPropLoading] = useState(false);
  const [propSaving, setPropSaving] = useState(false);
  const [propLoaded, setPropLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const org = await settingsApi.getOrg();
        setOrgRequire(org?.requireAttachmentOnRequisitionSubmit === true);
        setMaxMb(String(org?.maxAttachmentSizeMb || DEFAULT_MAX_MB));
      } catch (err) {
        toast.error(t('Could not load settings'), { description: err?.message });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [toast, t]);

  const loadProperty = useCallback(async (propertyId) => {
    setSelectedProperty(propertyId);
    setPropLoaded(false);
    if (!propertyId) return;
    setPropLoading(true);
    try {
      const doc = await settingsApi.getPropertyCommercial(propertyId);
      setPropOverride(!!doc?.overrideRequisitionRules);
      setPropRequire(doc?.requireAttachmentOnRequisitionSubmit === true);
      setPropLoaded(true);
    } catch (err) {
      toast.error(t('Could not load property settings'), { description: err?.message });
    } finally {
      setPropLoading(false);
    }
  }, [toast, t]);

  const saveOrg = async () => {
    const parsed = Number(maxMb);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > ABSOLUTE_MAX_MB) {
      toast.error(t('Enter an upload limit between 1 and 200 MB.'));
      return;
    }
    setOrgSaving(true);
    try {
      const res = await settingsApi.updateOrgRequisitions({
        requireAttachmentOnRequisitionSubmit: orgRequire,
        maxAttachmentSizeMb: Math.round(parsed),
      });
      setOrgRequire(res?.requireAttachmentOnRequisitionSubmit === true);
      setMaxMb(String(res?.maxAttachmentSizeMb || DEFAULT_MAX_MB));
      toast.success(t('Requisition rules saved'));
    } catch (err) {
      toast.error(t('Could not save settings'), { description: err?.message });
    } finally {
      setOrgSaving(false);
    }
  };

  const saveProperty = async () => {
    if (!selectedProperty) return;
    setPropSaving(true);
    try {
      const res = await settingsApi.updatePropertyRequisitions(selectedProperty, {
        propertyId: selectedProperty,
        overrideRequisitionRules: propOverride,
        requireAttachmentOnRequisitionSubmit: propRequire,
      });
      setPropOverride(!!res?.overrideRequisitionRules);
      setPropRequire(res?.requireAttachmentOnRequisitionSubmit === true);
      toast.success(t('Property overrides saved'));
    } catch (err) {
      toast.error(t('Could not save overrides'), { description: err?.message });
    } finally {
      setPropSaving(false);
    }
  };

  if (orgLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <section>
        <h2 className="appr-scope__title">{t('Organization defaults')}</h2>
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Supporting file on requisitions')}</h3>
                <p className="set-section__hint">
                  {t('When on, a requisition must carry at least one file — a photo of the item, a supplier quote or a specification — before it can be submitted for approval. Any file type counts, so approvers are never asked to decide with nothing to look at.')}
                </p>
              </div>
              <Switch
                checked={orgRequire}
                onChange={setOrgRequire}
                label={t('Require a file before a requisition can be submitted')}
              />
            </div>

            <div style={{ marginTop: 'var(--space-5)' }}>
              <h3 className="set-section__title">{t('Upload size limit')}</h3>
              <p className="set-section__hint">
                {t('The largest single file anyone can upload, anywhere in the system — requisition files, receiving photos, invoice scans, supplier documents and catalogue images. Applies to the whole organization.')}
              </p>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 220, maxWidth: 240, marginTop: 'var(--space-3)' }}>
                <span className="appr-doc__ccy-label">{t('Maximum file size (MB)')}</span>
                <Input
                  type="number"
                  min="1"
                  max={ABSOLUTE_MAX_MB}
                  step="1"
                  value={maxMb}
                  onChange={(e) => setMaxMb(e.target.value)}
                  placeholder={t('Default: {n} MB', { n: DEFAULT_MAX_MB })}
                  aria-label={t('Maximum file size (MB)')}
                />
              </label>
            </div>

            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="primary" size="sm" loading={orgSaving} onClick={saveOrg}>{t('Save')}</Button>
            </div>
          </Card.Body>
        </Card>
      </section>

      <section>
        <h2 className="appr-scope__title">{t('Property overrides')}</h2>
        {!propsLoaded ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
        ) : properties.length === 0 ? (
          <EmptyState
            icon={<Icon.Building size={28} />}
            title={t('No properties yet')}
            description={t('Once a property exists, you can override the organization requisition rules for it.')}
          />
        ) : (
          <>
            <div className="set-property-pick">
              <Select
                options={[{ value: '', label: t('Select a property…') }, ...properties.map((p) => ({ value: p.id, label: p.name }))]}
                value={selectedProperty}
                onChange={loadProperty}
                ariaLabel={t('Property')}
                searchable
              />
            </div>

            {!selectedProperty ? (
              <Banner tone="info">{t('Choose a property to override its requisition rules. Left as inherit, it uses the organization defaults.')}</Banner>
            ) : propLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
            ) : propLoaded ? (
              <Card>
                <Card.Body>
                  <div className="set-section__head">
                    <div>
                      <h3 className="set-section__title">{t('Property override')}</h3>
                      <p className="set-section__hint">{t('When off, this property inherits the organization requisition rules.')}</p>
                    </div>
                    <Switch checked={propOverride} onChange={setPropOverride} label={t('Override')} />
                  </div>
                  {propOverride ? (
                    <div style={{ marginTop: 'var(--space-4)' }}>
                      <Switch
                        checked={propRequire}
                        onChange={setPropRequire}
                        label={t('Require a file before a requisition can be submitted')}
                      />
                    </div>
                  ) : (
                    <p className="set-inherit" style={{ marginTop: 'var(--space-4)' }}>
                      {t('Inherits the organization requisition rules.')}
                    </p>
                  )}
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <Button variant="primary" size="sm" loading={propSaving} onClick={saveProperty}>{t('Save')}</Button>
                  </div>
                  <p className="set-section__hint" style={{ marginTop: 'var(--space-3)' }}>
                    {t('The upload size limit is organization-wide and cannot be overridden per property.')}
                  </p>
                </Card.Body>
              </Card>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
